import { useState } from "react";
import { toast } from "sonner";
import {
  MarketingLayout, Eyebrow, Display, SectionTitle, FocalSentence, Body,
  PrimaryCTA, Section, Hairline,
} from "./MarketingLayout";

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("A name, an email and a message — that's all.");
      return;
    }
    setBusy(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: name.split(" ")[0] || name,
          last_name: name.split(" ").slice(1).join(" "),
          email, country: "", household_size: "",
          interested_plan: "Contact",
          referral_source: "contact-form",
          notes: message,
          beta_interest: false, marketing_consent: false,
        }),
      });
      setSent(true);
    } catch {
      toast.error("Could not send just now. Please email us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingLayout testid="contact-page">
      <Section testid="contact-hero" className="pt-24 sm:pt-32 pb-16">
        <div className="max-w-[820px]">
          <Eyebrow>Contact</Eyebrow>
          <Display className="mt-6">A real person will read this.</Display>
          <FocalSentence className="mt-8 max-w-[46ch]">
            Ideas, questions, quiet frustrations — all welcome.
          </FocalSentence>
        </div>
      </Section>

      <Hairline />

      <Section testid="contact-form-section">
        {sent ? (
          <div className="max-w-[52ch]" data-testid="contact-form-sent">
            <Eyebrow>Received</Eyebrow>
            <SectionTitle className="mt-6">Thank you — we’ll be in touch.</SectionTitle>
            <Body className="mt-6">
              Someone from the team will respond within a few days. If it’s
              urgent, you can also write to us at
              {" "}<a href="mailto:hello@arahome.co" style={{ borderBottom: '1px solid var(--ara-ink, #1F1F1F)' }}>hello@arahome.co</a>.
            </Body>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-x-16 gap-y-8">
            <div>
              <Eyebrow>Reach the team</Eyebrow>
              <Body className="mt-6 max-w-[36ch]">
                We answer everything ourselves. There are no support tickets
                to submit, no first-line agents, no queues.
              </Body>
              <Body className="mt-6 max-w-[36ch]">
                Prefer email? Write to{" "}
                <a href="mailto:hello@arahome.co" style={{ color: 'var(--ara-ink, #1F1F1F)', borderBottom: '1px solid var(--ara-ink, #1F1F1F)' }}>
                  hello@arahome.co
                </a>.
              </Body>
            </div>
            <form onSubmit={submit} className="max-w-[54ch] space-y-8" data-testid="contact-form">
              <Field label="Your name" value={name} onChange={setName} testid="contact-name" />
              <Field label="Email" type="email" value={email} onChange={setEmail} testid="contact-email" />
              <div>
                <label className="text-[11px] tracking-[0.14em] uppercase" style={{ color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 500 }}>
                  Message
                </label>
                <textarea
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Say hello, ask something, share a thought…"
                  className="mt-2 w-full rounded-lg px-3 py-2 outline-none resize-none"
                  style={{ background: 'transparent', border: '1px solid var(--ara-line, rgba(31,31,31,0.15))', color: 'var(--ara-ink, #1F1F1F)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px' }}
                  data-testid="contact-message"
                />
              </div>
              <PrimaryCTA type="submit" onClick={submit} testid="contact-submit">
                {busy ? "Sending…" : "Send message →"}
              </PrimaryCTA>
            </form>
          </div>
        )}
      </Section>

      <Section testid="contact-cta" className="pb-32">
        <FocalSentence className="max-w-[52ch]">
          The best conversations we’ve had came from families writing to tell us what was still missing.
        </FocalSentence>
      </Section>
    </MarketingLayout>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] tracking-[0.14em] uppercase" style={{ color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 500 }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent outline-none py-2 text-[15px]"
        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink, #1F1F1F)', borderBottom: '1px solid var(--ara-line, rgba(31,31,31,0.15))' }}
        data-testid={testid}
      />
    </div>
  );
}
